import {
  Contact,
  APIContact,
  ContactGroup,
  CheckType,
  ResourceType,
  isAllowed,
  Feature,
  Action,
} from '@super-software-inc/foundation'
import {
  FlexRow,
  LoadingIndicator,
  SortableTableHeaderCell,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
  Tooltip,
  TruncatedText,
} from 'components/lib'
import { orderBy } from 'lodash'
import React, { useMemo, useState } from 'react'
import { MdErrorOutline } from 'react-icons/md'
import { useRecoilState, useRecoilValue } from 'recoil'
import {
  authenticatedUserAtom,
  getDefaultAPIContact,
  getDefaultPropertyInfo,
  profileModalAtom,
} from 'state/atoms'
import styled from 'styled-components/macro'
import formatPhoneNumber from 'utils/formatPhoneNumber'
import { getContactByContactId } from 'api/contacts'
import {
  primaryAssociationSelector,
  selectedAssociationChoicesAtom,
} from '../../../AppRoutes'
import ContactAvatar from '../ContactAvatar'
import AccessDropdown from '../ProfileSidebar/AccessDropdown'
import ContactActionDropdown from '../ProfileSidebar/ContactActionDropdown'
import useContactTitle from './useContactTitle'
import useContactUnits from './useContactUnits'

const ResidentsContent = styled.div`
  padding: 12px 20px;
  margin: 20px 0;
  display: flex;
  flex-direction: column;
`
const Residents = ({
  contacts,
  isLoading,
  corpFirst = false,
}: {
  contacts: APIContact[]
  isLoading: boolean
  corpFirst?: boolean
}) => {
  const selectedAssociationChoices = useRecoilValue(
    selectedAssociationChoicesAtom,
  )

  const primaryAssociation = useRecoilValue(primaryAssociationSelector)

  const [profileModal, setProfileModal] = useRecoilState(profileModalAtom)

  const authenticatedUser = useRecoilValue(authenticatedUserAtom)

  const newResident: APIContact = {
    ...getDefaultAPIContact(),
    associationIds: [primaryAssociation.id],
    propertyInfo: [
      {
        ...getDefaultPropertyInfo(),
        groups: [ContactGroup.Residents],
        associationId: primaryAssociation.id,
      },
    ],
  }

  const { contactUnitNameMap } = useContactUnits(
    contacts,
    primaryAssociation,
    corpFirst,
  )

  const { contactTitleMap } = useContactTitle(
    contacts,
    primaryAssociation,
    corpFirst,
  )

  type SortKeys =
    | 'name'
    | 'title'
    | 'units'
    | 'email'
    | 'phone.number'
    | 'accessLevel'

  const [sortKey, setSortKey] = useState<SortKeys>('name')
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc')

  const handleSort = (nextSort: SortKeys) => {
    if (nextSort === sortKey) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')
    } else {
      setSortKey(nextSort)
      setSortOrder('asc')
    }
  }

  const sortedContacts = useMemo(() => {
    if (sortKey === 'name') {
      return orderBy(contacts, ['firstName', 'lastName'], sortOrder)
    }

    if (sortKey === 'units') {
      return orderBy(
        contacts,
        contact => contactUnitNameMap[contact.id],
        sortOrder,
      )
    }

    if (sortKey === 'title') {
      return orderBy(
        contacts,
        contact => contactTitleMap[contact.id],
        sortOrder,
      )
    }

    return orderBy(contacts, sortKey, sortOrder)
  }, [contacts, sortKey, sortOrder, contactUnitNameMap, contactTitleMap])

  const handleTableRowClick = async (contact: APIContact) => {
    // get full contact from db and set it
    const fullContact = await getContactByContactId(
      authenticatedUser.selectedCompany.id,
      contact.id,
    )
    setProfileModal({
      ...profileModal,
      sidebarIsOpen: true,
      selectedContact: fullContact,
      corpFirst,
    })
  }

  return (
    <ResidentsContent>
      <Table>
        <TableHead>
          <TableRow>
            <SortableTableHeaderCell
              active={sortKey === 'name'}
              sortOrder={sortOrder}
              onClick={() => handleSort('name')}
            >
              <FlexRow align="center">
                <TruncatedText>Name</TruncatedText>
              </FlexRow>
            </SortableTableHeaderCell>
            <SortableTableHeaderCell
              active={sortKey === 'units'}
              sortOrder={sortOrder}
              onClick={() => handleSort('units')}
              style={{ width: 160 }}
            >
              <FlexRow align="center">
                {corpFirst ? 'Location' : 'Units'}
              </FlexRow>
            </SortableTableHeaderCell>
            <SortableTableHeaderCell
              active={sortKey === 'title'}
              sortOrder={sortOrder}
              onClick={() => handleSort('title')}
              style={{ width: 160 }}
            >
              <FlexRow align="center">Type</FlexRow>
            </SortableTableHeaderCell>
            {!corpFirst && (
              <SortableTableHeaderCell
                active={sortKey === 'accessLevel'}
                sortOrder={sortOrder}
                onClick={() => handleSort('accessLevel')}
                style={{ width: 150 }}
              >
                <FlexRow align="center">Access Level</FlexRow>
              </SortableTableHeaderCell>
            )}
            <SortableTableHeaderCell
              active={sortKey === 'email'}
              sortOrder={sortOrder}
              onClick={() => handleSort('email')}
            >
              <FlexRow align="center">
                <TruncatedText>Email</TruncatedText>
              </FlexRow>
            </SortableTableHeaderCell>
            <SortableTableHeaderCell
              active={sortKey === 'phone.number'}
              sortOrder={sortOrder}
              onClick={() => handleSort('phone.number')}
              style={{ width: 160 }}
            >
              <FlexRow align="center">
                <TruncatedText>Phone</TruncatedText>
              </FlexRow>
            </SortableTableHeaderCell>
            {!corpFirst && <TableHeaderCell style={{ width: 50 }} />}
          </TableRow>
        </TableHead>
        <TableBody>
          {sortedContacts &&
            sortedContacts.length > 0 &&
            sortedContacts.map(contact => (
              <TableRow key={contact.id}>
                <TableCell onClick={() => handleTableRowClick(contact)}>
                  <FlexRow align="center">
                    <ContactAvatar
                      data={contact as Contact}
                      style={{ marginRight: 5 }}
                    />
                    <TruncatedText className="font-medium">
                      {contact.firstName}
                      {contact.lastName ? ` ${contact.lastName}` : ''}
                    </TruncatedText>
                  </FlexRow>
                </TableCell>
                <TableCell
                  onClick={() => handleTableRowClick(contact)}
                  className="text-slate-500"
                >
                  <FlexRow align="center">
                    <TruncatedText>
                      {contactUnitNameMap[contact.id]}
                    </TruncatedText>
                  </FlexRow>
                </TableCell>
                <TableCell
                  style={{ width: 160 }}
                  onClick={() => handleTableRowClick(contact)}
                  className="text-slate-500"
                >
                  <FlexRow align="center">
                    {contactTitleMap[contact.id]}
                  </FlexRow>
                </TableCell>
                {!corpFirst && (
                  <TableCell style={{ width: 120 }}>
                    <FlexRow align="center">
                      <AccessDropdown contact={contact} isSidebar={false} />
                    </FlexRow>
                  </TableCell>
                )}
                <TableCell
                  onClick={() => handleTableRowClick(contact)}
                  className="text-slate-500"
                >
                  <FlexRow align="center">
                    {contact.email && contact.email.length > 1 ? (
                      <TruncatedText>{contact.email}</TruncatedText>
                    ) : (
                      <Tooltip overlay={<span>No email on file</span>}>
                        <MdErrorOutline
                          style={{ fontSize: 16, color: '#DC6803' }}
                        />
                      </Tooltip>
                    )}
                  </FlexRow>
                </TableCell>
                <TableCell
                  onClick={() => handleTableRowClick(contact)}
                  style={{ width: 160 }}
                  className="text-slate-500"
                >
                  <FlexRow align="center">
                    <TruncatedText>
                      {contact.phone?.number &&
                        formatPhoneNumber(contact.phone?.number)}
                    </TruncatedText>
                  </FlexRow>
                </TableCell>
                {!corpFirst && (
                  <TableCell>
                    <ContactActionDropdown contact={contact} />
                  </TableCell>
                )}
              </TableRow>
            ))}
          {isLoading && (
            <TableRow>
              <TableCell colSpan={6}>
                <LoadingIndicator />
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
      {contacts.length === 0 && (
        <>
          <FlexRow style={{ marginTop: 50, marginBottom: 10 }} justify="center">
            <h3>No residents to display</h3>
          </FlexRow>
          {!corpFirst &&
            selectedAssociationChoices.length === 1 &&
            isAllowed(
              authenticatedUser.selectedContact,
              authenticatedUser.acts,
              [selectedAssociationChoices[0].id],
              ResourceType.PROPERTY,
              Feature.TASKS,
              Action.CREATE,
              CheckType.ANY,
            ) && (
              <FlexRow justify="center">
                <h6>
                  <span
                    onClick={() => {
                      setProfileModal({
                        ...profileModal,
                        editProfileIsOpen: true,
                        selectedContact: newResident,
                        corpFirst,
                      })
                    }}
                    style={{ textDecoration: 'underline', cursor: 'pointer' }}
                  >
                    Add your first owner, renter, or resident
                  </span>{' '}
                  to get started.
                </h6>
              </FlexRow>
            )}
        </>
      )}
    </ResidentsContent>
  )
}

export default Residents
